import { DotText } from "@/v2-backoffice-client/components/dot"
import {
  formatCompactAmount,
  formatInvoiceAmount,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { LicensePlanTermsQuery } from "@/v2-backoffice-client/license/plan/view/__types__/license.plan.terms.query"

type LicensePlanTermRow = LicensePlanTermsQuery["getLicensePlanTerms"][number]

// Fenerum states intervals as type+count ('month', 6). Spell out the common
// ones the way a sales rep says them.
function formatTermInterval(
  type: string | null | undefined,
  count: number | null | undefined,
) {
  if (!type) return "—"
  const n = count ?? 1
  if (type === "year") return n === 1 ? "Yearly" : `Every ${n} years`
  if (type === "month") {
    if (n === 1) return "Monthly"
    if (n === 3) return "Quarterly"
    return `Every ${n} months`
  }
  if (type === "day") return n === 1 ? "Daily" : `Every ${n} days`
  return `${type} × ${n}`
}

export type LicensePlanTermsTableProps = {
  terms: LicensePlanTermRow[]
  isLoading?: boolean
}

// The Revenue tab's "List prices" section: the license's billing terms with the
// live book per term. Read-only by design — Fenerum owns prices.
function LicensePlanTermsTable(props: LicensePlanTermsTableProps) {
  const { terms, isLoading } = props

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold">List prices</h3>
        <span className="text-xs text-muted-foreground">
          Fenerum owns these — change them there.
        </span>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading list prices…</p>
      ) : terms.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No billing terms mirrored for this license.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">Billed</th>
                <th className="px-3 py-2 text-right font-medium">List price</th>
                <th className="px-3 py-2 text-right font-medium">
                  Live subscriptions
                </th>
                <th className="px-3 py-2 text-right font-medium">MRR</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((term) => (
                <tr key={term.Id} className="border-b last:border-b-0">
                  <td className="px-3 py-2">
                    {formatTermInterval(term.IntervalType, term.IntervalCount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {term.Price != null
                      ? formatInvoiceAmount(term.Price, term.Currency)
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {term.SubscriptionCount}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {term.Mrr != null
                      ? formatCompactAmount(term.Mrr, term.Currency)
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <DotText variant={term.IsActive ? "success-1" : "muted"}>
                      {term.IsActive ? "Active" : "Retired"}
                    </DotText>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default LicensePlanTermsTable
